import {
  Activity,
  AlertTriangle,
  ArrowUpRight,
  CalendarDays,
  CheckCircle2,
  CircleDot,
  ClipboardCheck,
  Clock3,
  FileText,
  Globe2,
  LayoutDashboard,
  Linkedin,
  ListChecks,
  RadioTower,
  RefreshCw,
  ScrollText,
  Send,
  Settings,
  Share2,
} from "lucide-react";

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import type {
  ComponentType,
} from "react";

import ContentCalendarView from "./components/ContentCalendarView";

import {
  CONTENT_OPERATIONS_QUEUE_CATEGORIES,
  fetchContentOperationsQueue,
  type ContentOperationsQueueCategory,
  type ContentOperationsQueueResponse,
} from "./lib/contentOperationsQueue";

type IconComponent =
  ComponentType<{
    "aria-hidden"?: boolean;
    className?: string;
    size?: number;
    strokeWidth?: number;
  }>;

type DashboardView =
  | "overview"
  | "calendar";

interface NavigationItem {
  label: string;
  icon: IconComponent;
  view?: DashboardView;
}

interface QueueCard {
  category:
    ContentOperationsQueueCategory;

  label: string;
  description: string;
  icon: IconComponent;
  tone:
    | "danger"
    | "warning"
    | "info"
    | "success"
    | "primary"
    | "cyan"
    | "violet";
}

interface WorkflowStep {
  label: string;
  description: string;
  state:
    | "complete"
    | "current"
    | "waiting";
}

const navigationItems:
  NavigationItem[] = [
    {
      label:
        "Overview",

      icon:
        LayoutDashboard,

      view:
        "overview",
    },

    {
      label:
        "Content Calendar",

      icon:
        CalendarDays,

      view:
        "calendar",
    },

    {
      label:
        "Articles",

      icon:
        FileText,
    },

    {
      label:
        "Publishing",

      icon:
        Send,
    },

    {
      label:
        "Audit History",

      icon:
        ScrollText,
    },

    {
      label:
        "System Status",

      icon:
        Activity,
    },
  ];

const queueCards:
  QueueCard[] = [
    {
      category:
        "overdue_calendar_items",

      label:
        "Overdue",

      description:
        "Calendar items whose target publication time has passed.",

      icon:
        AlertTriangle,

      tone:
        "danger",
    },

    {
      category:
        "due_this_week",

      label:
        "Due this week",

      description:
        "Planned content approaching its publication window.",

      icon:
        Clock3,

      tone:
        "warning",
    },

    {
      category:
        "preparation_ready",

      label:
        "Preparation ready",

      description:
        "Approved topics ready to enter article production.",

      icon:
        ClipboardCheck,

      tone:
        "success",
    },

    {
      category:
        "drafts_waiting_for_review",

      label:
        "Waiting for review",

      description:
        "Drafts awaiting controlled editorial review.",

      icon:
        ListChecks,

      tone:
        "info",
    },

    {
      category:
        "approved_waiting_for_website",

      label:
        "Waiting for website",

      description:
        "Approved articles awaiting website publication.",

      icon:
        Globe2,

      tone:
        "primary",
    },

    {
      category:
        "website_waiting_for_buffer",

      label:
        "Waiting for Buffer",

      description:
        "Website articles requiring social distribution setup.",

      icon:
        Share2,

      tone:
        "cyan",
    },

    {
      category:
        "buffer_waiting_for_linkedin_confirmation",

      label:
        "Waiting for LinkedIn",

      description:
        "Buffer records awaiting LinkedIn publication evidence.",

      icon:
        Linkedin,

      tone:
        "violet",
    },
  ];

const queueCategoryLabels:
  Record<
    ContentOperationsQueueCategory,
    string
  > = {
    overdue_calendar_items:
      "Overdue",

    due_this_week:
      "Due this week",

    preparation_ready:
      "Preparation ready",

    drafts_waiting_for_review:
      "Waiting for review",

    approved_waiting_for_website:
      "Waiting for website",

    website_waiting_for_buffer:
      "Waiting for Buffer",

    buffer_waiting_for_linkedin_confirmation:
      "Waiting for LinkedIn",
  };

const workflowSteps:
  WorkflowStep[] = [
    {
      label:
        "Calendar",

      description:
        "Topic and target date",

      state:
        "complete",
    },

    {
      label:
        "Preparation",

      description:
        "Research and content brief",

      state:
        "complete",
    },

    {
      label:
        "Editorial review",

      description:
        "Quality and brand approval",

      state:
        "current",
    },

    {
      label:
        "Website",

      description:
        "Publication confirmation",

      state:
        "waiting",
    },

    {
      label:
        "Buffer",

      description:
        "Social distribution",

      state:
        "waiting",
    },

    {
      label:
        "LinkedIn",

      description:
        "Final publication evidence",

      state:
        "waiting",
    },
  ];

function formatLocalTime(
  value: Date,
): string {
  return new Intl.DateTimeFormat(
    "en-CA",
    {
      hour:
        "2-digit",

      minute:
        "2-digit",

      timeZoneName:
        "short",
    },
  ).format(
    value,
  );
}

function formatQueueTimestamp(
  value: string | null,
): string {
  if (
    !value
  ) {
    return "Unscheduled";
  }

  const date =
    new Date(
      value,
    );

  if (
    Number.isNaN(
      date.getTime(),
    )
  ) {
    return "Unknown time";
  }

  return new Intl.DateTimeFormat(
    "en-CA",
    {
      month:
        "short",

      day:
        "numeric",

      hour:
        "2-digit",

      minute:
        "2-digit",
    },
  ).format(
    date,
  );
}

function App() {
  const [
    activeView,
    setActiveView,
  ] =
    useState<DashboardView>(
      window.location.hash ===
        "#content-calendar"
        ? "calendar"
        : "overview",
    );

  const navigateTo =
    useCallback(
      (
        view: DashboardView,
      ) => {
        setActiveView(
          view,
        );

        window.history.replaceState(
          null,
          "",
          view ===
            "calendar"
            ? "#content-calendar"
            : "#overview",
        );

        window.scrollTo({
          top:
            0,

          behavior:
            "smooth",
        });
      },
      [],
    );

  useEffect(
    () => {
      const handleHashChange =
        () => {
          setActiveView(
            window.location.hash ===
              "#content-calendar"
              ? "calendar"
              : "overview",
          );
        };

      window.addEventListener(
        "hashchange",
        handleHashChange,
      );

      return () => {
        window.removeEventListener(
          "hashchange",
          handleHashChange,
        );
      };
    },
    [],
  );

  const [
    queue,
    setQueue,
  ] =
    useState<
      ContentOperationsQueueResponse |
      null
    >(
      null,
    );

  const [
    queueError,
    setQueueError,
  ] =
    useState<
      string |
      null
    >(
      null,
    );

  const [
    isLoading,
    setIsLoading,
  ] =
    useState(
      true,
    );

  const [
    lastSyncedAt,
    setLastSyncedAt,
  ] =
    useState<
      Date |
      null
    >(
      null,
    );

  const loadQueue =
    useCallback(
      async (
        signal?: AbortSignal,
      ) => {
        setIsLoading(
          true,
        );

        setQueueError(
          null,
        );

        try {
          const result =
            await fetchContentOperationsQueue(
              signal,
            );

          setQueue(
            result,
          );

          setLastSyncedAt(
            new Date(),
          );
        } catch (
          error
        ) {
          if (
            signal?.aborted
          ) {
            return;
          }

          setQueueError(
            error instanceof
              Error
              ? error.message
              : "The production queue could not be loaded.",
          );
        } finally {
          if (
            !signal?.aborted
          ) {
            setIsLoading(
              false,
            );
          }
        }
      },
      [],
    );

  useEffect(
    () => {
      const controller =
        new AbortController();

      void loadQueue(
        controller.signal,
      );

      return () => {
        controller.abort();
      };
    },
    [
      loadQueue,
    ],
  );

  const priorityItems =
    useMemo(
      () => {
        if (
          !queue
        ) {
          return [];
        }

        return CONTENT_OPERATIONS_QUEUE_CATEGORIES
          .flatMap(
            (
              category,
            ) =>
              queue.queues[
                category
              ].map(
                (
                  item,
                ) => ({
                  category,
                  item,
                }),
              ),
          )
          .slice(
            0,
            8,
          );
      },
      [
        queue,
      ],
    );

  const updatedAt =
    lastSyncedAt
      ? formatLocalTime(
          lastSyncedAt,
        )
      : "Not synchronized";

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand-lockup">
          <img
            src="/images/brand/vex-telecom-logo.png"
            alt="VEX Telecom"
            className="brand-logo"
          />

          <div className="brand-copy">
            <span className="brand-section">
              Content Operations
            </span>

            <span className="brand-subtitle">
              Production control centre
            </span>
          </div>
        </div>

        <div className="sidebar-divider" />

        <nav
          className="sidebar-navigation"
          aria-label="Dashboard navigation"
        >
          <span className="navigation-label">
            Workspace
          </span>

          {navigationItems.map(
            (
              item,
            ) => {
              const Icon =
                item.icon;

              return (
                <button
                  key={item.label}
                  type="button"
                  className={
                    item.view ===
                    activeView
                      ? "navigation-item navigation-item-active"
                      : "navigation-item"
                  }
                  aria-current={
                    item.view ===
                    activeView
                      ? "page"
                      : undefined
                  }
                  disabled={!item.view}
                  title={
                    item.view
                      ? item.label
                      : `${item.label} will be added in a later build.`
                  }
                  onClick={() => {
                    if (
                      item.view
                    ) {
                      navigateTo(
                        item.view,
                      );
                    }
                  }}
                >
                  <Icon
                    aria-hidden
                    size={18}
                    strokeWidth={1.8}
                  />

                  <span>
                    {item.label}
                  </span>
                </button>
              );
            },
          )}
        </nav>

        <div className="sidebar-spacer" />

        <section className="sidebar-status glass">
          <div className="sidebar-status-heading">
            <RadioTower
              aria-hidden
              size={17}
              strokeWidth={1.9}
            />

            <span>
              Content Engine
            </span>
          </div>

          <div className="system-online-row">
            <span className="online-indicator" />

            <span>
              Production online
            </span>
          </div>

          <p>
            Protected workflow API connected to the VEX Telecom
            production environment.
          </p>
        </section>

        <button
          type="button"
          className="sidebar-settings-button"
        >
          <Settings
            aria-hidden
            size={17}
            strokeWidth={1.8}
          />

          <span>
            Workspace settings
          </span>
        </button>
      </aside>

      <main className="main-panel">
        <header className="topbar">
          <div>
            <span className="topbar-eyebrow">
              VEX Telecom
            </span>

            <span className="topbar-title">
              {activeView ===
              "calendar"
                ? "Content Calendar"
                : "Content Operations"}
            </span>
          </div>

          <div className="topbar-actions">
            <div className="environment-badge">
              <span className="online-indicator" />

              Production
            </div>

            <button
              type="button"
              className={
                isLoading
                  ? "icon-button is-loading"
                  : "icon-button"
              }
              aria-label="Refresh operations data"
              title="Refresh operations data"
              disabled={isLoading}
              onClick={() => {
                void loadQueue();
              }}
            >
              <RefreshCw
                aria-hidden
                size={17}
                strokeWidth={1.9}
              />
            </button>
          </div>
        </header>

        <div className="dashboard-content">
          {activeView ===
          "calendar" ? (
            <ContentCalendarView
              onBack={() => {
                navigateTo(
                  "overview",
                );
              }}
            />
          ) : (
            <>
          <section className="hero-panel grid-bg">
            <div className="hero-glow" />

            <div className="hero-content">
              <div className="hero-copy">
                <div className="section-kicker">
                  <CircleDot
                    aria-hidden
                    size={15}
                    strokeWidth={2}
                  />

                  Live production workspace
                </div>

                <h1>
                  Content operations
                  <span> control centre</span>
                </h1>

                <p>
                  Plan, review, publish and monitor VEX Telecom
                  content from one controlled production workspace.
                </p>
              </div>

              <div className="hero-status">
                <span className="hero-status-label">
                  Operational state
                </span>

                <div
                  className={
                    queueError
                      ? "hero-status-value hero-status-value-error"
                      : "hero-status-value"
                  }
                >
                  {isLoading ? (
                    <RefreshCw
                      aria-hidden
                      size={21}
                      strokeWidth={2}
                      className="is-spinning"
                    />
                  ) : queueError ? (
                    <AlertTriangle
                      aria-hidden
                      size={21}
                      strokeWidth={2}
                    />
                  ) : (
                    <CheckCircle2
                      aria-hidden
                      size={21}
                      strokeWidth={2}
                    />
                  )}

                  {isLoading
                    ? "Synchronizing"
                    : queueError
                      ? "Connection attention"
                      : "Systems ready"}
                </div>

                <span className="hero-status-time">
                  {queueError
                    ? "Queue synchronization failed."
                    : "Last synchronized " +
                      updatedAt}
                </span>
              </div>
            </div>
          </section>

          <section
            className="section-heading"
            aria-labelledby="queue-overview-title"
          >
            <div>
              <span className="section-eyebrow">
                Production queue
              </span>

              <h2 id="queue-overview-title">
                Operations overview
              </h2>

              <p>
                {isLoading
                  ? "Synchronizing the live production queue."
                  : queueError
                    ? "Live queue data is temporarily unavailable."
                    : queue
                      ? queue.totalActionItems +
                        " active production action item" +
                        (queue.totalActionItems ===
                        1
                          ? "."
                          : "s.")
                      : "No queue data is available."}
              </p>
            </div>

            <button
              type="button"
              className="secondary-button"
            >
              View full queue

              <ArrowUpRight
                aria-hidden
                size={16}
                strokeWidth={1.9}
              />
            </button>
          </section>

          <section className="metric-grid">
            {queueCards.map(
              (
                card,
              ) => {
                const Icon =
                  card.icon;

                const value =
                  queue?.counts[
                    card.category
                  ] ??
                  0;

                const statusLabel =
                  isLoading
                    ? "Syncing"
                    : queueError
                      ? "Offline"
                      : "Live";

                return (
                  <article
                    key={card.label}
                    className="metric-card hover-lift"
                    data-tone={card.tone}
                  >
                    <div className="metric-card-heading">
                      <div className="metric-icon">
                        <Icon
                          aria-hidden
                          size={19}
                          strokeWidth={1.8}
                        />
                      </div>

                      <span className="metric-status">
                        {statusLabel}
                      </span>
                    </div>

                    <strong className="metric-value">
                      {String(
                        value,
                      ).padStart(
                        2,
                        "0",
                      )}
                    </strong>

                    <h3>
                      {card.label}
                    </h3>

                    <p>
                      {card.description}
                    </p>

                    <button
                      type="button"
                      className="metric-link"
                    >
                      View queue

                      <ArrowUpRight
                        aria-hidden
                        size={15}
                        strokeWidth={1.8}
                      />
                    </button>
                  </article>
                );
              },
            )}
          </section>

          <section className="dashboard-grid">
            <article className="panel workflow-panel">
              <div className="panel-heading">
                <div>
                  <span className="section-eyebrow">
                    Production workflow
                  </span>

                  <h2>
                    Controlled publishing path
                  </h2>
                </div>

                <span className="panel-badge">
                  Audit protected
                </span>
              </div>

              <div className="workflow-list">
                {workflowSteps.map(
                  (
                    step,
                    index,
                  ) => (
                    <div
                      key={step.label}
                      className="workflow-step"
                      data-state={step.state}
                    >
                      <div className="workflow-marker-column">
                        <span className="workflow-marker" />

                        {index <
                          workflowSteps.length -
                            1 && (
                          <span className="workflow-line" />
                        )}
                      </div>

                      <div className="workflow-step-copy">
                        <strong>
                          {step.label}
                        </strong>

                        <span>
                          {step.description}
                        </span>
                      </div>

                      <span className="workflow-state">
                        {step.state ===
                        "complete"
                          ? "Complete"
                          : step.state ===
                              "current"
                            ? "Current"
                            : "Waiting"}
                      </span>
                    </div>
                  ),
                )}
              </div>
            </article>

            <article className="panel queue-panel">
              <div className="panel-heading">
                <div>
                  <span className="section-eyebrow">
                    Priority work
                  </span>

                  <h2>
                    Production activity
                  </h2>
                </div>

                <Activity
                  aria-hidden
                  size={20}
                  strokeWidth={1.8}
                  className="panel-heading-icon"
                />
              </div>

              {isLoading ? (
                <div className="empty-state">
                  <div className="empty-state-icon">
                    <RefreshCw
                      aria-hidden
                      size={24}
                      strokeWidth={1.8}
                      className="is-spinning"
                    />
                  </div>

                  <h3>
                    Synchronizing production
                  </h3>

                  <p>
                    Loading current calendar, article and publishing
                    workflow state.
                  </p>
                </div>
              ) : queueError ? (
                <div className="empty-state empty-state-error">
                  <div className="empty-state-icon">
                    <AlertTriangle
                      aria-hidden
                      size={24}
                      strokeWidth={1.8}
                    />
                  </div>

                  <h3>
                    Queue connection unavailable
                  </h3>

                  <p>
                    {queueError}
                  </p>

                  <button
                    type="button"
                    className="primary-button"
                    onClick={() => {
                      void loadQueue();
                    }}
                  >
                    Retry connection

                    <RefreshCw
                      aria-hidden
                      size={16}
                      strokeWidth={1.9}
                    />
                  </button>
                </div>
              ) : priorityItems.length > 0 ? (
                <div className="priority-list">
                  {priorityItems.map(
                    ({
                      category,
                      item,
                    }) => (
                      <article
                        key={item.queueItemId}
                        className="priority-item"
                      >
                        <div className="priority-item-heading">
                          <span className="priority-category">
                            {queueCategoryLabels[
                              category
                            ]}
                          </span>

                          <span className="priority-date">
                            {formatQueueTimestamp(
                              item.targetPublishAt ??
                                item.updatedAt,
                            )}
                          </span>
                        </div>

                        <h3>
                          {item.title}
                        </h3>

                        <p>
                          {item.reason}
                        </p>

                        <div className="priority-meta">
                          <span>
                            {item.source}
                          </span>

                          {item.priority ? (
                            <span>
                              {item.priority} priority
                            </span>
                          ) : null}

                          {item.articleStatus ? (
                            <span>
                              {item.articleStatus.replaceAll(
                                "_",
                                " ",
                              )}
                            </span>
                          ) : null}
                        </div>

                        {item.articleUrl ? (
                          <a
                            href={item.articleUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="priority-link"
                          >
                            Open published article

                            <ArrowUpRight
                              aria-hidden
                              size={15}
                              strokeWidth={1.8}
                            />
                          </a>
                        ) : null}
                      </article>
                    ),
                  )}
                </div>
              ) : (
                <div className="empty-state">
                  <div className="empty-state-icon">
                    <CheckCircle2
                      aria-hidden
                      size={24}
                      strokeWidth={1.8}
                    />
                  </div>

                  <h3>
                    No urgent work detected
                  </h3>

                  <p>
                    The production queue currently contains no overdue
                    or blocked content operations.
                  </p>

                  <button
                    type="button"
                    className="primary-button"
                  >
                    Open content calendar

                    <ArrowUpRight
                      aria-hidden
                      size={16}
                      strokeWidth={1.9}
                    />
                  </button>
                </div>
              )}
            </article>
          </section>

          <footer className="dashboard-footer">
            <span>
              VEX Telecom Content Operations
            </span>

            <span>
              Secure production workspace
            </span>
          </footer>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default App;
